import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { fetchPlatformSettingsPublic } from '@/store/actions/creators/platformSettingsActions';
import { platformSettingsPublicSelector } from '@/store/selectors/platformSettingsSelector';
import { isPublicPath, loadGtm, pushEvent } from '@/utils/gtm';

/**
 * Injects the GTM snippet on public pages (login, register, cgu, etc.)
 * once the platform settings have been fetched and a GTM container ID
 * is configured by the SuperAdmin. No-op on logged-in routes.
 *
 * Mounted once at the top of the App tree — does not render anything.
 */
export function GtmInjector() {
  const dispatch = useDispatch();
  const location = useLocation();
  const settings = useSelector(platformSettingsPublicSelector) as any;
  const containerId = settings?.gtmContainerId;

  // Pull settings once. AppShell already fetches them when authenticated;
  // doing it here makes anonymous pages work too.
  useEffect(() => {
    if (!settings) {
      // @ts-expect-error legacy redux-thunk action types
      dispatch(fetchPlatformSettingsPublic());
    }
  }, []);

  // Inject GTM on first navigation to a public path (idempotent).
  useEffect(() => {
    if (!containerId) return;
    if (!isPublicPath(location.pathname)) return;
    loadGtm(containerId);
  }, [containerId, location.pathname]);

  // Auto-pushed page_view on every public-route change once GTM is up.
  useEffect(() => {
    if (!isPublicPath(location.pathname)) return;
    pushEvent('public_page_view', {
      path: location.pathname,
      search: location.search,
    });
  }, [location.pathname, location.search]);

  return null;
}
